'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  FileText,
  User,
  ShieldCheck,
} from 'lucide-react';
import type { Teacher, AttendanceRecord, AttendanceStatus, DeportmentStatus } from '@/lib/mockData';

interface FormValues {
  date: string;
  status: AttendanceStatus;
  checkIn: string;
  checkOut: string;
  deportment: DeportmentStatus;
  deportmentViolationType: string;
  deportmentNote: string;
  absenceReason: string;
  notes: string;
  substituteArranged: boolean;
  substituteName: string;
}

const VIOLATION_TYPES = [
  'Casual dress not aligned with professional code',
  'Visible tattoos — breach of dress code policy',
  'Open-toed shoes — policy violation',
  'Inappropriate hairstyle',
  'Excessive jewellery',
  'No staff ID badge worn',
  'Other — see notes',
];

interface Props {
  teacher: Teacher;
  existingRecord?: AttendanceRecord;
}

export default function AttendanceEntryForm({ teacher, existingRecord }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      date: existingRecord?.date ?? '2026-05-14',
      status: existingRecord?.status ?? 'Present',
      checkIn: existingRecord?.checkIn ?? '',
      checkOut: existingRecord?.checkOut ?? '',
      deportment: existingRecord?.deportment ?? 'Compliant',
      deportmentViolationType: '',
      deportmentNote: existingRecord?.deportmentNote ?? '',
      absenceReason: '',
      notes: existingRecord?.notes ?? '',
      substituteArranged: false,
      substituteName: '',
    },
  });

  const watchStatus = watch('status');
  const watchDeportment = watch('deportment');
  const watchSubstitute = watch('substituteArranged');

  const showCheckInOut = watchStatus === 'Present' || watchStatus === 'Late' || watchStatus === 'Excused';
  const showAbsenceFields = watchStatus === 'Absent';
  const showDeportmentViolation = watchDeportment === 'Non-Compliant' || watchDeportment === 'Warning Issued';

  // Backend integration point: POST /api/attendance/record
  async function onSubmit(data: FormValues) {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSaved(true);
    toast.success(`Attendance logged for ${teacher.name}`, {
      description: `${data.status} · ${data.date} · Deportment: ${data.deportment}`,
    });
    setTimeout(() => setSaved(false), 3000);
  }

  const isExisting = !!existingRecord;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-card border border-border rounded-xl overflow-hidden"
      noValidate
    >
      {/* Form Header */}
      <div className="px-6 py-4 border-b border-border bg-muted/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <User size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-600 text-foreground" style={{ fontWeight: 600 }}>{teacher.name}</h3>
            <p className="text-xs text-muted-foreground">{teacher.subject} · {teacher.employeeNumber}</p>
          </div>
        </div>
        {isExisting && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-500" style={{ fontWeight: 500 }}>
            <AlertTriangle size={11} />
            Editing existing record
          </span>
        )}
      </div>

      {/* Unsaved changes indicator */}
      {isDirty && !saved && (
        <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs text-amber-700">
          <AlertTriangle size={12} />
          You have unsaved changes — scroll down and save before navigating away.
        </div>
      )}

      {/* Saved success indicator */}
      {saved && (
        <div className="px-6 py-2 bg-green-50 border-b border-green-200 flex items-center gap-2 text-xs text-green-700 fade-in">
          <CheckCircle2 size={12} />
          Record saved successfully.
        </div>
      )}

      <div className="px-6 py-5 space-y-6">
        {/* Section 1: Date & Attendance */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={14} className="text-primary" />
            <h4 className="text-xs font-600 uppercase tracking-wide text-muted-foreground" style={{ fontWeight: 600 }}>
              Attendance Details
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Date */}
            <div>
              <label htmlFor="entry-date" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                Date <span className="text-red-500">*</span>
              </label>
              <p className="text-[10px] text-muted-foreground mb-1.5">The school day this record applies to.</p>
              <input
                id="entry-date"
                type="date"
                {...register('date', { required: 'Date is required' })}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {errors.date && (
                <p className="text-xs text-red-600 mt-1" role="alert">{errors.date.message}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label htmlFor="entry-status" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                Attendance Status <span className="text-red-500">*</span>
              </label>
              <p className="text-[10px] text-muted-foreground mb-1.5">Select the teacher&apos;s attendance classification for this day.</p>
              <select
                id="entry-status"
                {...register('status', { required: 'Status is required' })}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
                <option value="Late">Late</option>
                <option value="Excused">Excused</option>
                <option value="On Leave">On Leave</option>
              </select>
              {errors.status && (
                <p className="text-xs text-red-600 mt-1" role="alert">{errors.status.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Check-In / Check-Out */}
        {showCheckInOut && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock size={14} className="text-primary" />
              <h4 className="text-xs font-600 uppercase tracking-wide text-muted-foreground" style={{ fontWeight: 600 }}>
                Check-In / Check-Out Times
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="entry-checkin" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Check-In Time <span className="text-red-500">*</span>
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  Contracted start: {teacher.contractedStart}. Times after 07:30 are flagged as late.
                </p>
                <input
                  id="entry-checkin"
                  type="time"
                  {...register('checkIn', {
                    required: showCheckInOut ? 'Check-in time is required' : false,
                  })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                />
                {errors.checkIn && (
                  <p className="text-xs text-red-600 mt-1" role="alert">{errors.checkIn.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="entry-checkout" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Check-Out Time <span className="text-red-500">*</span>
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  Contracted end: {teacher.contractedEnd}. Early departures will be flagged.
                </p>
                <input
                  id="entry-checkout"
                  type="time"
                  {...register('checkOut', {
                    required: showCheckInOut ? 'Check-out time is required' : false,
                  })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                />
                {errors.checkOut && (
                  <p className="text-xs text-red-600 mt-1" role="alert">{errors.checkOut.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section 3: Absence Details */}
        {showAbsenceFields && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-600" />
              <h4 className="text-xs font-600 uppercase tracking-wide text-red-700" style={{ fontWeight: 600 }}>
                Absence Details
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="absence-reason" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Reason for Absence
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">Record any reason provided by the teacher or HOD.</p>
                <select
                  id="absence-reason"
                  {...register('absenceReason')}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">— Select reason —</option>
                  <option value="Sick leave — certificate submitted">Sick leave — certificate submitted</option>
                  <option value="Sick leave — no certificate">Sick leave — no certificate</option>
                  <option value="Family emergency">Family emergency</option>
                  <option value="Bereavement leave">Bereavement leave</option>
                  <option value="Study leave — approved">Study leave — approved</option>
                  <option value="No reason provided">No reason provided</option>
                  <option value="Other — see notes">Other — see notes</option>
                </select>
              </div>

              <div>
                <label htmlFor="substitute-arranged" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Substitute Arranged?
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">Indicate whether a substitute teacher covered the classes.</p>
                <div className="flex items-center gap-3 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
                    <input type="checkbox" {...register('substituteArranged')} className="rounded border-border" />
                    Yes — substitute arranged
                  </label>
                </div>
              </div>

              {watchSubstitute && (
                <div className="sm:col-span-2">
                  <label htmlFor="substitute-name" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                    Substitute Teacher Name
                  </label>
                  <input
                    id="substitute-name"
                    type="text"
                    placeholder="Full name of substitute teacher"
                    {...register('substituteName')}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 4: Deportment */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={14} className="text-primary" />
            <h4 className="text-xs font-600 uppercase tracking-wide text-muted-foreground" style={{ fontWeight: 600 }}>
              Deportment Compliance
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="entry-deportment" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                Deportment Status <span className="text-red-500">*</span>
              </label>
              <p className="text-[10px] text-muted-foreground mb-1.5">
                Assess the teacher&apos;s adherence to the school dress code and professional appearance policy.
              </p>
              <select
                id="entry-deportment"
                {...register('deportment', { required: 'Deportment status is required' })}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="Compliant">Compliant</option>
                <option value="Warning Issued">Warning Issued</option>
                <option value="Non-Compliant">Non-Compliant</option>
              </select>
              {errors.deportment && (
                <p className="text-xs text-red-600 mt-1" role="alert">{errors.deportment.message}</p>
              )}
            </div>

            {showDeportmentViolation && (
              <div>
                <label htmlFor="violation-type" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Violation Type <span className="text-red-500">*</span>
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">Select the specific dress code or conduct violation observed.</p>
                <select
                  id="violation-type"
                  {...register('deportmentViolationType', {
                    required: showDeportmentViolation ? 'Please specify the violation type' : false,
                  })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">— Select violation —</option>
                  {VIOLATION_TYPES.map((v) => (
                    <option key={`viol-${v.slice(0, 20)}`} value={v}>{v}</option>
                  ))}
                </select>
                {errors.deportmentViolationType && (
                  <p className="text-xs text-red-600 mt-1" role="alert">{errors.deportmentViolationType.message}</p>
                )}
              </div>
            )}

            {showDeportmentViolation && (
              <div className="sm:col-span-2">
                <label htmlFor="deportment-note" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
                  Deportment Notes
                </label>
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  Provide specific details for the violation to support any formal disciplinary process.
                </p>
                <textarea
                  id="deportment-note"
                  rows={2}
                  placeholder="Describe the specific violation observed…"
                  {...register('deportmentNote')}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Section 5: General Notes */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FileText size={14} className="text-primary" />
            <h4 className="text-xs font-600 uppercase tracking-wide text-muted-foreground" style={{ fontWeight: 600 }}>
              General Notes
            </h4>
          </div>
          <div>
            <label htmlFor="entry-notes" className="block text-xs font-500 text-foreground mb-1" style={{ fontWeight: 500 }}>
              Additional Notes
            </label>
            <p className="text-[10px] text-muted-foreground mb-1.5">
              Any additional observations or context relevant to this record. This note appears in the principal&apos;s dashboard view.
            </p>
            <textarea
              id="entry-notes"
              rows={3}
              placeholder="Optional — any additional context for this record…"
              {...register('notes')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>

        {/* Required fields note */}
        <p className="text-[10px] text-muted-foreground">
          <span className="text-red-500">*</span> Required fields. All entries are logged with your admin account and timestamp.
        </p>
      </div>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 px-6 py-4 border-t border-border bg-card flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => reset()}
          className="px-4 py-2 text-sm font-500 text-muted-foreground border border-border rounded-lg hover:bg-muted hover:text-foreground transition-colors"
          style={{ fontWeight: 500 }}
        >
          Reset Form
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 min-w-[140px] justify-center"
          style={{ fontWeight: 600 }}
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : saved ? (
            <>
              <CheckCircle2 size={14} />
              Saved
            </>
          ) : (
            <>
              <Save size={14} />
              {isExisting ? 'Update Record' : 'Save Attendance Record'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}